import type { HttpErrorResponse } from '@angular/common/http';
import type { Action } from '@ngrx/store';

import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, combineLatest, fromEvent, timer } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    exhaustMap,
    map,
    startWith,
    switchMap,
    take,
    takeUntil,
} from 'rxjs/operators';

import { isUuid } from '@common/helpers';
import NotifyAction from '@common/utils/notify/notify.actions';

import { AppFacade } from '@web/store/app/app.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { RouterFacade } from '@web/store/router/router.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { SiteService } from '@web/store/website/site/site.service';
import { PagesHttpService } from '@web/store/website/pages/pages.http.service';

import HistoryActions from '@web/store/history/history.actions';
import LayoutActions from '@web/store/layout/layout.actions';
import PagesActions from '@web/store/website/pages/pages.actions';
import ContentActions from './content.actions';
import { ContentFacade } from './content.facade';

/** How often an open page that the user can see checks whether it was changed somewhere else. */
export const PAGE_VERSION_POLL_INTERVAL = 5000;

/**
 * Picks up a write made somewhere else while the page is open — the AI agent through MCP, another
 * tab — by polling the page's `version`, which landings-service rotates on every write (#73735), and
 * re-reads the page when it moved. Without it such a write stays invisible until F5, and the next
 * autosave either overwrites it or, now that saves carry the version, fails with a 409.
 *
 * The server wins, as on a 409: unsaved edits here are dropped with the reload — they could not be
 * saved over the newer page anyway.
 *
 * Not covered: the theme and the header/footer live on the SITE, and a write that reaches them
 * through ANOTHER page leaves this page's version alone (§2.1 of #74421).
 */
@Injectable()
export class PageVersionSyncEffects {
    private readonly actions$ = inject(Actions);
    private readonly document = inject(DOCUMENT);
    private readonly translateService = inject(TranslateService);
    private readonly pagesHttpService = inject(PagesHttpService);
    private readonly contentFacade = inject(ContentFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly routerFacade = inject(RouterFacade);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly appFacade = inject(AppFacade);
    private readonly siteService = inject(SiteService);

    private readonly isVisible$ = fromEvent(this.document, 'visibilitychange').pipe(
        startWith(null),
        map(() => this.document.visibilityState === 'visible'),
        distinctUntilChanged()
    );

    public readonly syncPageVersion$ = createEffect(() =>
        // Coming back to the tab checks at once: the page may have been changed while it was hidden.
        this.isVisible$.pipe(
            switchMap((isVisible) => (isVisible ? timer(0, PAGE_VERSION_POLL_INTERVAL) : EMPTY)),
            concatLatestFrom(() => [
                this.routerFacade.routerParams$,
                this.contentFacade.pageVersions$,
                this.contentFacade.deletedPageIds$,
            ]),
            // A slow answer is waited for rather than stacked behind.
            exhaustMap(([, { siteId, pageId }, versions, deleted]) => {
                const known = versions[String(pageId)];

                if (!this.canPoll(siteId, pageId, known) || deleted.includes(String(pageId))) {
                    return EMPTY;
                }

                return this.isIdle().pipe(exhaustMap((isIdle) => (isIdle ? this.checkVersion(siteId, pageId) : EMPTY)));
            })
        )
    );

    /**
     * Only a saved page whose version the editor holds — none means landings-service does not version
     * pages yet, and there is nothing to compare. Not a template in the admin editor either.
     */
    private canPoll(siteId: string, pageId: string, known: string | undefined): boolean {
        return Boolean(siteId && pageId && known) && !isUuid(siteId) && !isUuid(pageId);
    }

    /**
     * Nothing the user is in the middle of: a reload would tear down a drag, a sentence being typed on
     * the canvas or an open dialog. Saving and paused-autosave states are skipped too — the first moves
     * the version itself, the second is an AI reply or a gesture that owns the page for its duration.
     */
    private isIdle() {
        return combineLatest([
            this.appFacade.isAdmin$,
            this.siteFacade.isSaving$,
            this.contentFacade.isLoading$,
            this.contentFacade.isAutoSavePaused$,
            this.layoutFacade.isEntityDragging$,
        ]).pipe(
            take(1),
            map(
                ([isAdmin, isSaving, isLoading, isAutoSavePaused, isDragging]) =>
                    !isAdmin &&
                    !isSaving &&
                    !isLoading &&
                    !isAutoSavePaused &&
                    !isDragging &&
                    !this.isTypingOnCanvas() &&
                    !this.document.body.classList.contains('modal-open')
            )
        );
    }

    private isTypingOnCanvas(): boolean {
        return Boolean(this.document.activeElement?.closest('[contenteditable]:not([contenteditable="false"])'));
    }

    private checkVersion(siteId: string, pageId: string) {
        return this.pagesHttpService.getSitePageInfo(siteId, pageId).pipe(
            // A save, or a load, of our own moves the version while the answer is on its way.
            takeUntil(
                this.actions$.pipe(
                    ofType(
                        PagesActions.updatePageRequest,
                        ContentActions.setPageVersion,
                        ContentActions.loadContent,
                        ContentActions.loadPageContentRequest
                    )
                )
            ),
            concatLatestFrom(() => [this.contentFacade.pageVersions$, this.routerFacade.routerParams$]),
            exhaustMap(([{ version }, versions, routerParams]) => {
                const known = versions[String(pageId)];

                if (!version || !known || version === known || String(routerParams.pageId) !== String(pageId)) {
                    return EMPTY;
                }

                return this.reload(siteId, pageId);
            }),
            catchError(({ status }: HttpErrorResponse) =>
                status === 404 ? this.siteService.pageDeletedActions(pageId) : EMPTY
            )
        );
    }

    /**
     * The site document is re-read too: the write that moved this page's version may have changed the
     * theme or the header/footer with it. History goes: undoing would put back what the other writer
     * replaced, and save it over them.
     */
    private reload(siteId: string, pageId: string): Action[] {
        return [
            NotifyAction.showNotify({
                toastType: 'info',
                message: this.translateService.instant('page_updated_elsewhere'),
            }),
            LayoutActions.hideSelectedEntitySettings(),
            HistoryActions.resetHistory(),
            ContentActions.setIsDirty({ isDirty: false }),
            ContentActions.loadPageContentRequest({ siteId, pageId, reloadSite: true }),
        ];
    }
}
